import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Message, SettingsCard } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { cn } from "../../utils/bem";
import { HFImportPanel } from "../../components/HFImportPanel/HFImportPanel";
import { requiredKeysFromConfig } from "../CreateProject/utils/labelConfig";
import "./HuggingFaceHubSettings.prefix.css";

/**
 * Project Settings → Hugging Face Hub.
 *
 * Structural mirror of QualitySettings.jsx. Every visual primitive
 * (field, label, input, radio card, checkbox, button) uses the same
 * class-name idiom and shared components (`Button`, `Checkbox`, `Message`,
 * `SettingsCard` from @humansignal/ui). Local BEM prefix is `hf-`.
 */

// Class names generated via ``cn()`` — .prefix.css files prepend ``lsf-`` to
// every selector in the compiled CSS, so raw string class names in JSX won't
// match. Route everything through ``cn().toClassName()``.
const settingsDescription = cn("settings-description").toClassName();
const settingsCaption = cn("settings-caption").toClassName();

const hf = (suffix) => cn(`hf-${suffix}`).toClassName();
const hfBlock = (block, element) => cn(`hf-${block}`).elem(element).toClassName();

const Section = ({ title, description, summary, onSave, saving, dirty, saveLabel = "Save", children }) => (
  <SettingsCard
    title={title}
    description={description}
    footer={
      (summary || onSave) && (
        <>
          <span className={settingsDescription} style={{ marginRight: "auto" }}>
            {summary}
          </span>
          {onSave && (
            <Button
              type="button"
              variant="primary"
              size="small"
              onClick={onSave}
              disabled={saving || !dirty}
              aria-label={`${saveLabel} ${title}`}
            >
              {saving ? "Saving…" : saveLabel}
            </Button>
          )}
        </>
      )
    }
  >
    <div className={hf("section-body")}>{children}</div>
  </SettingsCard>
);

const Field = ({ label, caption, children }) => (
  <div className={hf("field")}>
    {label && <label className={hfBlock("field", "label")}>{label}</label>}
    {caption && (
      <p className={settingsCaption} style={{ marginTop: 0 }}>
        {caption}
      </p>
    )}
    {children}
  </div>
);

// Select + dynamic description — one compact dropdown per decision,
// with the chosen option's explanation rendered underneath so the
// text updates as the user browses values. Disabled options stay in
// the list (greyed) so the capability is discoverable.
const SelectList = ({ value, onChange, options, disabled }) => {
  const current = options.find((o) => o.value === value);
  return (
    <>
      <select className={hf("input")} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.title}
            {opt.tag ? `. ${opt.tag}` : ""}
          </option>
        ))}
      </select>
      {current?.description && <p className={hf("select-desc")}>{current.description}</p>}
    </>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

const HFHubSettings = () => {
  const { project } = useContext(ProjectContext);
  const { user } = useAuth();
  const api = useAPI();

  const [hasTokenOverride, setHasTokenOverride] = useState(null);
  const hasPersonalToken = hasTokenOverride ?? !!user?.has_hf_token;
  // Effective access (own token, or the organisation's when the member's
  // role or a grant allows it), with the sentence to show when not.
  const [access, setAccess] = useState(null);
  const loadAccess = useCallback(async () => {
    if (!project?.id) return;
    const res = await api.callApi("hfAccess", { params: { pk: project.id }, errorFilter: () => true });
    if (res && !res.error) setAccess(res);
  }, [api, project?.id]);
  useEffect(() => {
    loadAccess();
  }, [loadAccess, hasPersonalToken]);
  const hasToken = access ? !!access.import : hasPersonalToken;

  // Backend returns { bindings: [ {source_repo_id, source_config,
  // source_split, columns, column_mapping}, ... ] }.
  const [bindings, setBindings] = useState([]);
  const loadSource = useCallback(async () => {
    if (!project?.id) return;
    const res = await api.callApi("hfSource", { params: { pk: project.id }, suppressError: true });
    if (res && !res.error) setBindings(Array.isArray(res.bindings) ? res.bindings : []);
  }, [api, project?.id]);
  useEffect(() => {
    loadSource();
  }, [loadSource]);

  const hasSource = bindings.length > 0;

  // Horizontal sub-tabs (owner-requested) — one concern per tab
  // instead of four stacked cards. Local state, no routes: the page
  // is already a leaf under project Settings.
  const [tab, setTab] = useState("import");
  const tabs = [
    { id: "token", label: "Access" },
    { id: "import", label: "Import" },
    { id: "push", label: "Push" },
  ];

  return (
    <div className={cn("settings-page").toClassName()}>
      <SettingsPageHeader
        title="Hugging Face Hub"
        description="Import data from Hugging Face, annotate it, then push annotations back, to the source dataset if you own it, or to a fork of your own."
      />
      <div role="tablist" aria-label="Hugging Face Hub sections" className={hf("tabs")}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`${hf("tab")}${tab === t.id ? ` ${cn("hf-tab").mod({ active: true }).toClassName()}` : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "token" && (
        <>
          <SettingsCard
            title="Your access"
            description="What you can do with Hugging Face in this project, and which token is used."
          >
            <p style={{ margin: 0 }} data-testid="hf-access-status">
              {access == null
                ? "Checking…"
                : access.import
                  ? access.source === "organization"
                    ? `Using the organisation's Hugging Face token. Import is on; pushing is ${access.push ? "on" : "off"}${access.push ? "" : ` (${access.push_message ?? ""})`}.`
                    : `Using your own Hugging Face token. Import is on; pushing is ${access.push ? "on" : `off (${access.push_message ?? ""})`}.`
                  : access.message}
            </p>
            {access && !access.import && access.can_manage_org_token && (
              <p style={{ margin: "8px 0 0" }}>
                <a href="/organization/settings?tab=Hugging%20Face">Set up the organisation token →</a>
              </p>
            )}
          </SettingsCard>
          <TokenStatusCard api={api} hasToken={hasPersonalToken} onTokenChange={setHasTokenOverride} />
        </>
      )}
      {tab === "import" && (
        <>
          <SourceBindingCard api={api} projectId={project?.id} bindings={bindings} onUnbound={loadSource} />
          <Section
            title="Import from Hub"
            description="Preview an HF dataset, pick the columns to bring in, and each row lands as a task."
          >
            <HFImportPanel
              projectId={project?.id}
              onImported={loadSource}
              requiredKeys={requiredKeysFromConfig(project?.label_config)}
            />
          </Section>
        </>
      )}
      {tab === "push" && (
        <PushCard api={api} projectId={project?.id} hasToken={hasToken} bindings={bindings} access={access} />
      )}
    </div>
  );
};

// ── 1. Token status ─────────────────────────────────────────────────────────

const TokenStatusCard = ({ api, hasToken, onTokenChange }) => {
  const [editing, setEditing] = useState(!hasToken);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);

  useEffect(() => {
    setEditing(!hasToken);
  }, [hasToken]);

  const saveToken = async () => {
    if (!draft.startsWith("hf_")) {
      setError(
        'Hugging Face tokens start with "hf_". Paste the full value from https://huggingface.co/settings/tokens.',
      );
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    const res = await api.callApi("hfSetToken", { body: { token: draft.trim() } });
    setBusy(false);
    if (!res || res.error) {
      setError(res?.response?.detail || res?.error || "Failed to save token.");
      return;
    }
    setDraft("");
    setEditing(false);
    setOk("Token saved.");
    onTokenChange?.(true);
  };

  const clearToken = async () => {
    setBusy(true);
    setError(null);
    setOk(null);
    const res = await api.callApi("hfClearToken");
    setBusy(false);
    if (!res || res.error) {
      setError(res?.response?.detail || res?.error || "Failed to clear token.");
      return;
    }
    setEditing(true);
    setOk("Token cleared.");
    onTokenChange?.(false);
  };

  // Body-only layout that mirrors LicensingSettings' CurrentLicenseCard —
  // description carries the state ("Connected." / "Not set."), body is
  // either a subtle Replace/Clear action row or the input+save flow.
  return (
    <SettingsCard
      title="Hugging Face token"
      description={
        hasToken
          ? "Connected. Stored server-side and never returned in API responses."
          : "Paste your Hugging Face personal access token to enable import + push. Stored server-side and never returned in API responses."
      }
    >
      {hasToken && !editing && (
        <div className={hf("row-inline")}>
          <Button
            type="button"
            look="outlined"
            size="small"
            onClick={() => setEditing(true)}
            disabled={busy}
            style={{ minWidth: 96 }}
          >
            Replace
          </Button>
          <Button
            type="button"
            look="outlined"
            variant="negative"
            size="small"
            onClick={clearToken}
            disabled={busy}
            style={{ minWidth: 96 }}
          >
            Clear
          </Button>
        </div>
      )}
      {editing && (
        <>
          <Field
            label="Personal access token"
            caption='Generate one at huggingface.co/settings/tokens, "read" for import, "write" if you also want to push.'
          >
            <input
              type="password"
              className={hf("input")}
              placeholder="hf_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy}
              autoComplete="off"
              style={{ maxWidth: 420 }}
            />
          </Field>
          <div className={hf("row-inline")}>
            <Button
              style={{ width: 120 }}
              type="button"
              variant="primary"
              size="small"
              onClick={saveToken}
              disabled={!draft.trim() || busy}
            >
              {busy ? "Saving…" : "Save"}
            </Button>
            {hasToken && (
              <Button
                type="button"
                look="outlined"
                size="small"
                onClick={() => {
                  setEditing(false);
                  setDraft("");
                  setError(null);
                }}
                disabled={busy}
              >
                Cancel
              </Button>
            )}
          </div>
        </>
      )}
      {error && <Message look="danger">{error}</Message>}
      {ok && <Message look="success">{ok}</Message>}
    </SettingsCard>
  );
};

// ── 2. Source binding ────────────────────────────────────────────────────────

const SourceBindingCard = ({ api, projectId, bindings, onUnbound }) => {
  const [busyIdx, setBusyIdx] = useState(null);
  const [confirmingIdx, setConfirmingIdx] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const doUnbind = async (idx) => {
    setBusyIdx(idx);
    const res = await api.callApi("hfSourceClear", {
      params: { pk: projectId },
      query: { binding_index: idx },
      suppressError: true,
    });
    setBusyIdx(null);
    setConfirmingIdx(null);
    if (res && !res.error) {
      setLastResult({
        tasks: res.tasks_deleted ?? 0,
        repos: res.source_repo_ids || [],
      });
      onUnbound?.();
    }
  };

  return (
    <Section
      title="Source datasets"
      description={
        bindings.length > 0
          ? `This project is imported from ${bindings.length} Hugging Face dataset${bindings.length === 1 ? "" : "s"}. Annotations can be pushed back to any of them (if you have write access) or forked into new repos.`
          : "No source dataset bound yet. Import from Hub below to create a binding, or use fresh export to push this project as a new HF dataset."
      }
    >
      {bindings.length > 0 ? (
        <>
          <div className={hf("bindings-grid")}>
            {bindings.map((b, idx) => {
              const mapping = b?.column_mapping || {};
              const mappingEntries = Object.entries(mapping);
              const busy = busyIdx === idx;
              const confirming = confirmingIdx === idx;
              return (
                <div key={`${b.source_repo_id}-${idx}`} className={hf("binding-card")}>
                  <dl className={hf("dl")}>
                    <dt>Dataset</dt>
                    <dd>
                      <a
                        href={`https://huggingface.co/datasets/${b.source_repo_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {b.source_repo_id}
                      </a>
                    </dd>
                    <dt>Config</dt>
                    <dd>{b.source_config || "(default)"}</dd>
                    <dt>Split</dt>
                    <dd>{b.source_split || "train"}</dd>
                    <dt>Columns imported</dt>
                    <dd>{(b.columns || []).join(", ") || "(none)"}</dd>
                    {mappingEntries.length > 0 && (
                      <>
                        <dt>Column mapping</dt>
                        <dd>
                          {mappingEntries.map(([hfCol, lsField]) => (
                            <div key={hfCol} className={hf("mapping-row")}>
                              <code>{hfCol}</code>
                              <span aria-hidden> → </span>
                              <code>{lsField}</code>
                            </div>
                          ))}
                        </dd>
                      </>
                    )}
                  </dl>
                  <div className={hf("row-inline")}>
                    {confirming ? (
                      <>
                        <Button
                          type="button"
                          look="outlined"
                          variant="negative"
                          size="small"
                          onClick={() => doUnbind(idx)}
                          disabled={busy}
                          style={{ minWidth: 128 }}
                        >
                          {busy ? "Unbinding…" : "Confirm unbind"}
                        </Button>
                        <Button
                          type="button"
                          look="outlined"
                          size="small"
                          onClick={() => setConfirmingIdx(null)}
                          disabled={busy}
                          style={{ minWidth: 96 }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        look="outlined"
                        variant="negative"
                        size="small"
                        onClick={() => setConfirmingIdx(idx)}
                        disabled={busyIdx !== null}
                        style={{ minWidth: 96 }}
                      >
                        Unbind
                      </Button>
                    )}
                  </div>
                  {confirming && (
                    <p className={settingsCaption} style={{ marginTop: 8 }}>
                      <strong>
                        This deletes every task imported from <code>{b.source_repo_id}</code>, along with their
                        annotations, drafts, and predictions.
                      </strong>{" "}
                      Tasks that came from other sources (upload, cloud storage, other HF datasets on this project) are
                      untouched. Not reversible.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {lastResult && (
            <Message look="success">
              Unbound
              {lastResult.repos.length > 0 && (
                <>
                  {" "}
                  from <code>{lastResult.repos.join(", ")}</code>
                </>
              )}
              . {lastResult.tasks} task{lastResult.tasks === 1 ? "" : "s"} deleted from the Data Manager.
            </Message>
          )}
        </>
      ) : (
        <p className={settingsCaption} style={{ margin: 0 }}>
          No binding.
        </p>
      )}
    </Section>
  );
};

// ── 4. Push to Hub ───────────────────────────────────────────────────────────

const PushCard = ({ api, projectId, hasToken, bindings, access }) => {
  const hasSource = bindings.length > 0;
  // The access endpoint folds in org policies (pushes disabled, licence
  // first, exports to managers, data stays in own buckets) and the
  // organisation-token push rules, with one sentence explaining a refusal.
  const pushForbidden = access ? !access.push : false;
  const pushReason = access?.push_message ?? access?.message ?? "";
  const [mode, setMode] = useState("fresh");
  const [repoId, setRepoId] = useState("");
  const [privateFlag, setPrivateFlag] = useState(true);
  const [aggregation, setAggregation] = useState("majority");
  const [includeProvenance, setIncludeProvenance] = useState(false);
  // Speech-corpus options (plan G.2/G.3) — fresh pushes only.
  const [audioFormat, setAudioFormat] = useState("original");
  const [splitStrategy, setSplitStrategy] = useState("none");
  const [bindingIndex, setBindingIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const source = hasSource ? bindings[Math.min(bindingIndex, bindings.length - 1)] : null;

  useEffect(() => {
    if (mode === "enrich-source" && source) setRepoId(source.source_repo_id);
  }, [mode, source]);

  // Keep bindingIndex valid when bindings shrink (e.g. after unbind).
  useEffect(() => {
    if (bindingIndex >= bindings.length && bindings.length > 0) {
      setBindingIndex(0);
    }
  }, [bindings.length, bindingIndex]);

  const modeOptions = useMemo(
    () => [
      {
        value: "fresh",
        title: "Fresh export (new dataset)",
        description: "Full export (tasks + annotations) to a new dataset.",
      },
      {
        value: "enrich-source",
        title: "Enrich source dataset",
        tag: "needs write access",
        description: "Adds an annotation column to the source repo.",
        disabled: !hasSource,
      },
      {
        value: "fork",
        title: "Fork with annotations",
        description: "Source + annotations, pushed to a repo you own.",
        disabled: !hasSource,
      },
    ],
    [hasSource],
  );

  const canPush = hasToken && !pushForbidden && !busy && (mode === "enrich-source" ? hasSource : !!repoId.trim());

  const doPush = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    const body = {
      mode,
      repo_id: repoId.trim(),
      private: privateFlag,
      aggregation,
      include_provenance: includeProvenance,
    };
    if (mode === "fresh") {
      body.audio_format = audioFormat;
      body.split_strategy = splitStrategy;
    }
    if (mode !== "fresh" && hasSource) {
      body.binding_index = bindingIndex;
    }
    const res = await api.callApi("hfPush", {
      params: { pk: projectId },
      body,
      // Prevent the platform-wide error modal; render inline instead.
      suppressError: true,
    });
    setBusy(false);
    if (!res || res.error || res.$meta?.ok === false) {
      const raw = String(res?.response?.detail || res?.error || "");
      const lower = raw.toLowerCase();
      let msg;
      if (lower.includes("nothing to push") || lower.includes("no annotations") || lower.includes("no tasks")) {
        msg = "Nothing to push yet, annotate at least one task in this project first, then come back to push.";
      } else if (
        lower.includes("hf token") ||
        lower.includes("hugging face personal") ||
        lower.includes("access token")
      ) {
        msg = "Set your Hugging Face token in Account Settings → Hugging Face before pushing.";
      } else if (lower.includes("write access") || lower.includes("403") || lower.includes("401")) {
        msg =
          "Your Hugging Face token doesn't have write access to that repo. Regenerate a token with write scope and try again.";
      } else if (raw) {
        msg = `Push didn't finish: ${raw}`;
      } else {
        msg = "Push didn't finish. Check your network and that the Hugging Face Hub is reachable.";
      }
      setError(msg);
      return;
    }
    setResult(res);
  };

  const aggregationOptions = useMemo(
    () => [
      { value: "majority", title: "Majority vote", description: "Most-voted label wins. Best default." },
      { value: "first", title: "First submitted", description: "First annotation per task." },
      { value: "adjudicator", title: "Reviewer-adjudicated", description: "Reviewer's ground truth, else majority." },
      { value: "raw", title: "Raw (every annotator)", description: "One row per annotator." },
    ],
    [],
  );

  return (
    <>
      <Section title="Destination" description="Where this push lands on the Hub.">
        <Field label="Push mode">
          <SelectList value={mode} onChange={setMode} options={modeOptions} disabled={busy} />
        </Field>
        {mode !== "fresh" && bindings.length > 1 && (
          <Field label="Source binding" caption="Pick which bound dataset this push aligns to.">
            <select
              className={hf("input")}
              value={bindingIndex}
              onChange={(e) => setBindingIndex(Number(e.target.value))}
              disabled={busy}
            >
              {bindings.map((b, idx) => (
                <option key={`${b.source_repo_id}-${idx}`} value={idx}>
                  {b.source_repo_id}
                  {b.source_config ? `, ${b.source_config}` : ""}
                  {b.source_split ? `, ${b.source_split}` : ""}
                </option>
              ))}
            </select>
          </Field>
        )}
        <div className={hf("row")}>
          <Field
            label="Target repo id"
            caption={mode === "enrich-source" ? "Locked to the source repo in enrich mode." : "owner/name"}
          >
            <input
              type="text"
              className={hf("input")}
              placeholder={
                mode === "fresh" ? "you/your-dataset" : mode === "fork" ? "you/your-fork" : source?.source_repo_id || ""
              }
              value={repoId}
              onChange={(e) => setRepoId(e.target.value)}
              disabled={busy || mode === "enrich-source"}
            />
          </Field>
        </div>
        <label className={hf("checkrow")}>
          <Checkbox checked={privateFlag} onChange={(e) => setPrivateFlag(e?.target?.checked ?? !privateFlag)} />
          <span>Create as private repo</span>
        </label>
      </Section>

      <Section title="Annotations" description="How annotations land on each row.">
        <Field label="Aggregation strategy">
          <SelectList value={aggregation} onChange={setAggregation} options={aggregationOptions} disabled={busy} />
        </Field>
        <label className={hf("checkrow")}>
          <Checkbox
            checked={includeProvenance}
            onChange={(e) => setIncludeProvenance(e?.target?.checked ?? !includeProvenance)}
          />
          <span>
            Include raw <code>_annotations</code> sidecar column
          </span>
        </label>
      </Section>

      {mode === "fresh" && (
        <Section title="Speech options" description="Only used by audio projects.">
          <div className={hf("row")}>
            <Field label="Audio format">
              <SelectList
                value={audioFormat}
                onChange={setAudioFormat}
                disabled={busy}
                options={[
                  {
                    value: "original",
                    title: "Original URLs only",
                    tag: "default",
                    description: "Links to source audio; nothing embedded.",
                  },
                  {
                    value: "wav16k",
                    title: "Embed 16 kHz mono WAV",
                    description: "ASR-ready audio embedded per clip.",
                  },
                  {
                    value: "wav16k_norm",
                    title: "Embed 16 kHz mono WAV, RMS-normalized",
                    description: "Same, with loudness normalized.",
                  },
                ]}
              />
            </Field>
            <Field label="Splits">
              <SelectList
                value={splitStrategy}
                onChange={setSplitStrategy}
                disabled={busy}
                options={[
                  {
                    value: "none",
                    title: "Single split",
                    tag: "default",
                    description: "Everything lands in train.",
                  },
                  {
                    value: "speaker_prompt_disjoint",
                    title: "Speaker + prompt disjoint",
                    description: "80/10/10 with no speaker or sentence overlap.",
                  },
                ]}
              />
            </Field>
          </div>
        </Section>
      )}

      {error && <Message look="danger">{error}</Message>}
      {result && (
        <Message look="success">
          Pushed to{" "}
          <a href={result.repo_url} target="_blank" rel="noopener noreferrer">
            {result.repo_url}
          </a>
          {result.columns_added && result.columns_added.length > 0 && (
            <>
              , columns <code>{result.columns_added.join(", ")}</code>, {result.rows_annotated}/{result.rows_total}{" "}
              rows.
            </>
          )}
          {result.rows_pushed !== undefined && <>, {result.rows_pushed} rows pushed.</>}
          {result.splits && (
            <>
              {" "}
              Splits:{" "}
              {Object.entries(result.splits)
                .map(([k, v]) => `${k} ${v}`)
                .join(", ")}
              {result.rows_dropped_for_disjointness > 0 &&
                ` (${result.rows_dropped_for_disjointness} dropped for disjointness)`}
              .
            </>
          )}{" "}
          Aggregation: <code>{result.aggregation}</code>.
        </Message>
      )}

      <div className={hf("push-actions")}>
        <span className={settingsDescription} style={{ marginRight: "auto", alignSelf: "center" }}>
          A dataset card (README) is generated automatically from consent, QC, and split metadata.
        </span>
        {pushForbidden && (
          <p
            style={{
              margin: "0 0 8px",
              fontSize: "var(--font-size-body-small)",
              color: "var(--color-neutral-content-subtler, #64748b)",
            }}
          >
            {pushReason || "Pushing is not available to you in this project."}
          </p>
        )}
        <Button type="button" variant="primary" onClick={doPush} disabled={!canPush || pushForbidden}>
          {busy ? "Pushing…" : "Push to Hub"}
        </Button>
      </div>
    </>
  );
};

export const HuggingFaceHubSettings = {
  title: "Hugging Face Hub",
  path: "/hugging-face",
  component: HFHubSettings,
};

export default HFHubSettings;
